import React, { useState } from "react";
import { Dispatch, SetStateAction } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
  Modal,
  Button,
  Select,
  SelectChangeEvent,
  MenuItem,
  Box,
  FormHelperText,
} from "@mui/material";
import useTranslation from "../hooks/traslation";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useAddNotificationMutation } from "../services/notification";
import { showError, showToast } from "../constants/toasts";
import { NOTIFICATION_ROLE, NOTIFICATION_TYPE } from "../utils/enums";

interface AddNotificationModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSubmit: (values: NotificationFormValues) => Promise<void>;
  isLoading: boolean;
}

interface NotificationFormValues {
  title: string;
  role: string;
  type: string;
  message: string;
}

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .max(250, "Title must not exceed 250 characters")
    .test("no-only-spaces", "Title cannot contain only spaces", (value) => {
      if (!value) return true;
      return value.trim().length > 0;
    }),
  role: Yup.string().required("Please select an audience"),
  type: Yup.string().required("Please select a notification type"),
  message: Yup.string()
    .required("Message is required")
    .max(500, "Message must not exceed 500 characters")
    .test("no-only-spaces", "Message cannot contain only spaces", (value) => {
      if (!value) return true;
      return value.trim().length > 0;
    }),
});

export default function AddNotification({
  open,
  onClose,
  setOpen,
  onSubmit,
  isLoading,
}: AddNotificationModalProps) {
  const translation = useTranslation();

  const initialValues: NotificationFormValues = {
    title: "",
    role: "",
    type: "",
    message: "",
  };

  return (
    <Modal
      className="modal addNotification_modal"
      id="addNotificationModal"
      aria-labelledby="addNotification-modal-title"
      aria-describedby="addNotification-modal-description"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{translation.main.addNotification}</h2>
            <div className="btn-close" onClick={() => setOpen(false)}>
              <CloseIcon />
            </div>
          </div>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ errors, touched, handleChange, values, isSubmitting }) => (
              <Form className="form">
                <div className="gap_p">
                  <div className="control_group w_100">
                    <Field
                      as={TextField}
                      name="title"
                      variant="outlined"
                      placeholder={translation.globals.title}
                      fullWidth
                      hiddenLabel
                      error={touched.title && Boolean(errors.title)}
                      disabled={isSubmitting || isLoading}
                    />
                    {touched.title && errors.title && (
                      <FormHelperText
                        error
                        sx={{ color: "#ff0000 !important" }}
                      >
                        {errors.title}
                      </FormHelperText>
                    )}
                  </div>
                  <div className="control_group w_50">
                    <Field
                      as={Select}
                      name="role"
                      fullWidth
                      value={values.role}
                      onChange={handleChange}
                      error={touched.role && Boolean(errors.role)}
                      displayEmpty
                      disabled={isSubmitting || isLoading}
                    >
                      <MenuItem value="" disabled>
                        {translation.globals.select}
                      </MenuItem>
                      <MenuItem value={NOTIFICATION_ROLE.ALL}>
                        {translation.globals.All}
                      </MenuItem>
                      <MenuItem value={NOTIFICATION_ROLE.EMPLOYER}>
                        Employer
                      </MenuItem>
                      <MenuItem value={NOTIFICATION_ROLE.FREELANCER}>
                        Freelancers
                      </MenuItem>
                    </Field>
                    {touched.role && errors.role && (
                      <FormHelperText
                        error
                        sx={{ color: "#ff0000 !important" }}
                      >
                        {errors.role}
                      </FormHelperText>
                    )}
                  </div>
                  <div className="control_group w_50">
                    <Field
                      as={Select}
                      name="type"
                      fullWidth
                      value={values.type}
                      onChange={handleChange}
                      error={touched.type && Boolean(errors.type)}
                      displayEmpty
                      disabled={isSubmitting || isLoading}
                    >
                      <MenuItem value="" disabled>
                        {translation.main.selectType}
                      </MenuItem>
                      <MenuItem value={NOTIFICATION_TYPE.BROADCAST}>
                        Broadcast
                      </MenuItem>
                      <MenuItem value={NOTIFICATION_TYPE.MAIL}>Mail</MenuItem>
                      {/* <MenuItem value={NOTIFICATION_TYPE.PUSH}>Push</MenuItem> */}
                    </Field>
                    {touched.type && errors.type && (
                      <FormHelperText
                        error
                        sx={{ color: "#ff0000 !important" }}
                      >
                        {errors.type}
                      </FormHelperText>
                    )}
                  </div>
                  <div className="control_group w_100">
                    <Field
                      as={TextField}
                      name="message"
                      variant="outlined"
                      placeholder="Message"
                      fullWidth
                      hiddenLabel
                      multiline
                      maxRows={5}
                      minRows={5}
                      error={touched.message && Boolean(errors.message)}
                      disabled={isSubmitting || isLoading}
                    />
                    {touched.message && errors.message && (
                      <FormHelperText
                        error
                        sx={{ color: "#ff0000 !important" }}
                      >
                        {errors.message}
                      </FormHelperText>
                    )}
                  </div>
                </div>
                <div className="footer_btn_flex">
                  <Button
                    className="btn btn_grey"
                    onClick={() => setOpen(false)}
                    disabled={isSubmitting || isLoading}
                  >
                    {translation.globals.Cancel}
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn_primary"
                    disabled={isSubmitting || isLoading}
                  >
                    {isSubmitting || isLoading
                      ? "Submitting..."
                      : translation.globals.submit}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Modal>
  );
}
